import { createServerFn } from "@tanstack/react-start";
import { authMiddleware } from "#/modules/util.auth/middleware.auth";
import { getRequest, setResponseHeader } from "@tanstack/react-start/server";
import { Result } from "better-result";
import { getSociaalEmotioneleThemas } from "#/modules/sociaal-emotioneel.overview/api.sociaalemotioneel-overview.server";
import type { SocialEmotionalOverviewSearch } from "#/modules/sociaal-emotioneel.overview/schema.search";
import {
  createSociaalEmotioneelThemasReeks,
  deleteSociaalEmotioneelThemasReeks,
  getSociaalEmotioneelThemasReeksDetail,
  updateSociaalEmotioneelThemasReeksPublicatie,
  updateSociaalEmotioneelThemasReeks,
} from "#/modules/sociaal-emotioneel/api.sociaal-emotioneel.server";
import { getAdminSociaalEmotioneelThemasReeksen } from "#/modules/sociaal-emotioneel/api.sociaal-emotioneel-admin.server";
import {
  getSociaalEmotioneelThemasReeksDetailCookieFromRequest,
  SOCIAAL_EMOTIONEEL_THEMA_COOKIE_NAME,
} from "#/modules/sociaal-emotioneel/cookie.sociaal-emotioneel";
import { apiFailure } from "#/modules/util.errors/api-failure.server";
import { toFieldErrors, translateApiError } from "#/modules/util.errors/translate";
import { toastErrorMiddleware } from "#/modules/util.errors/middleware.toast-errors";
import { m } from "#/paraglide/messages";
import type { AdminSocialEmotionalSearch } from "#/modules/sociaal-emotioneel.overview/schema.search";
import type { components } from "#/schema";

export const getSchoolSocialEmotionalOverview = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .validator((data: SocialEmotionalOverviewSearch) => data)
  .handler(async ({ data }) => {
    const result = await getSociaalEmotioneleThemas({ ...data });
    if (Result.isError(result)) throw result.error;
    const response = result.value;

    const totalPages = Math.ceil((response.count || 0) / data.pageSize);

    return {
      sociaalEmotioneleThemas: response.data || [],
      totalPages,
      totalCount: response.count || 0,
    };
  });

export const getAdminSocialEmotionalOverview = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .validator((data: AdminSocialEmotionalSearch) => data)
  .handler(async ({ data }) => {
    const result = await getAdminSociaalEmotioneelThemasReeksen(data);
    if (Result.isError(result)) throw result.error;
    const response = result.value;
    const totalPages = Math.ceil((response.count || 0) / data.pageSize);

    return {
      socialEmotionalThemes: response.data || [],
      totalPages,
      totalCount: response.count || 0,
    };
  });

export const getSocialEmotionalAddPage = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .handler(async () => {
    const request = getRequest();
    return {
      sociaalEmotioneelThemasReeks: getSociaalEmotioneelThemasReeksDetailCookieFromRequest(request),
      isEditRoute: false,
    };
  });

export const getSocialEmotionalEditPage = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .validator((data: { id: string }) => data)
  .handler(async ({ data }) => {
    const result = await getSociaalEmotioneelThemasReeksDetail(data.id);

    if (Result.isError(result)) {
      throw result.error;
    }

    return {
      sociaalEmotioneelThemasReeks: result.value.sociaalEmotioneelThemasReeks,
      isEditRoute: true,
    };
  });

export const getSocialEmotionalDetail = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .validator((data: { id: string }) => data)
  .handler(async ({ data }) => {
    const result = await getSociaalEmotioneelThemasReeksDetail(data.id);

    if (Result.isError(result)) {
      throw result.error;
    }

    return {
      sociaalEmotioneelThemasReeks: result.value.sociaalEmotioneelThemasReeks,
    };
  });

export const submitCreateSocialEmotional = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator((data: { sociaalEmotioneelThemasReeks: unknown }) => data)
  .handler(async ({ data }) => {
    // SAFETY: the form posts its reeks detail model as-is; the backend validates every field.
    const sociaalEmotioneelThemasReeks =
      data.sociaalEmotioneelThemasReeks as components["schemas"]["SociaalEmotioneelThemasReeksDetailModel"];

    const result = await createSociaalEmotioneelThemasReeks(sociaalEmotioneelThemasReeks);

    if (Result.isError(result)) {
      const fieldErrors = toFieldErrors(result.error, {
        "sociaal_emotioneel_thema.sociaalEmotioneelThema": () =>
          m["sociaal_emotioneel_thema.api_validation_errors.sociaalEmotioneelThema_required"](),
      });
      return fieldErrors ? { fieldErrors } : { formError: translateApiError(result.error) };
    }

    setResponseHeader("Set-Cookie", `${SOCIAAL_EMOTIONEEL_THEMA_COOKIE_NAME}=; Max-Age=0; Path=/`);

    // Portal showed an "added" toast via remix-toast on the server redirect. GET/redirect cookie
    // writes don't persist here, so we return the message and let the caller toast then
    // navigate back to its own (school or admin) overview with a typed link.
    return {
      message: m["sociaal_emotioneel_thema.toast_messages.added"]({
        name: sociaalEmotioneelThemasReeks.name,
      }),
    };
  });

export const submitUpdateSocialEmotional = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator((data: { id: string; sociaalEmotioneelThemasReeks: unknown }) => data)
  .handler(async ({ data }) => {
    // SAFETY: the form posts its reeks detail model as-is; the backend validates every field.
    const sociaalEmotioneelThemasReeks =
      data.sociaalEmotioneelThemasReeks as components["schemas"]["SociaalEmotioneelThemasReeksDetailModel"];

    const loadResult = await getSociaalEmotioneelThemasReeksDetail(data.id);
    if (Result.isError(loadResult)) {
      throw loadResult.error;
    }

    const result = await updateSociaalEmotioneelThemasReeks(sociaalEmotioneelThemasReeks);

    if (Result.isError(result)) {
      const fieldErrors = toFieldErrors(result.error, {
        "sociaal_emotioneel_thema.sociaalEmotioneelThema": () =>
          m["sociaal_emotioneel_thema.api_validation_errors.sociaalEmotioneelThema_required"](),
      });
      return fieldErrors
        ? { fieldErrors }
        : {
            formError: translateApiError(result.error, {
              UpdateNotAllowed: () => m["sociaal_emotioneel_thema.api_errors.update_not_allowed"](),
            }),
          };
    }

    return {
      message: m["sociaal_emotioneel_thema.toast_messages.updated"]({
        name: sociaalEmotioneelThemasReeks.name,
      }),
    };
  });

export const submitDeleteSocialEmotional = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator((data: { id: string }) => data)
  .handler(async ({ data }) => {
    const loadResult = await getSociaalEmotioneelThemasReeksDetail(data.id);
    if (Result.isError(loadResult)) {
      throw loadResult.error;
    }
    const detail = loadResult.value;

    const result = await deleteSociaalEmotioneelThemasReeks(data.id);

    if (Result.isError(result)) {
      return { formError: translateApiError(result.error) };
    }

    // Portal showed a "deleted" toast via remix-toast on the server redirect; the caller toasts +
    // navigates (typed) here instead. Portal fell back to the id for the name — the port loads the
    // detail for the guard above, so we use the real name.
    return {
      message: m["sociaal_emotioneel_thema.toast_messages.deleted"]({
        name: detail.sociaalEmotioneelThemasReeks.name,
      }),
    };
  });

export const togglePublishSocialEmotional = createServerFn({ method: "POST" })
  .middleware([authMiddleware, toastErrorMiddleware])
  .validator((data: { reeksId: string; isPublished: boolean }) => data)
  .handler(async ({ data }) => {
    const result = await updateSociaalEmotioneelThemasReeksPublicatie(
      data.reeksId,
      !data.isPublished,
    );

    if (Result.isError(result)) {
      return apiFailure(result.error);
    }
  });
